<script setup lang="ts">
  import type { UploadCustomRequestOptions, UploadFileInfo } from 'naive-ui'
  import { useMessage } from 'naive-ui'

  const model = defineModel<string>({ default: '' })
  const uploading = defineModel<boolean>('uploading', { default: false })

  const props = withDefaults(
    defineProps<{
      disabled?: boolean
      tip?: string
      /** Max file size in bytes */
      maxSize?: number
      accept?: string
    }>(),
    {
      disabled: false,
      tip: '仅 1 张，支持 JPG / PNG / GIF / WEBP，不超过 10MB',
      maxSize: 10 * 1024 * 1024,
      accept: 'image/jpeg,image/png,image/gif,image/webp',
    },
  )

  const message = useMessage()
  const fileList = ref<UploadFileInfo[]>([])

  function syncFileList(url: string) {
    const next = url.trim()
    if (!next) {
      fileList.value = []
      return
    }
    const current = fileList.value[0]
    if (current?.status === 'finished' && current.url === next) return
    fileList.value = [
      {
        id: 'image',
        name: 'image',
        status: 'finished',
        url: next,
      },
    ]
  }

  watch(
    model,
    (url) => {
      syncFileList(url || '')
    },
    { immediate: true },
  )

  function beforeUpload(data: { file: UploadFileInfo }) {
    const raw = data.file.file
    if (!raw) return false
    if (raw.size > props.maxSize) {
      message.error('文件超出10mb，请重新上传')
      return false
    }
    return true
  }

  async function customRequest({ file, onFinish, onError }: UploadCustomRequestOptions) {
    const raw = file.file
    if (!raw) {
      onError()
      return
    }

    if (raw.size > props.maxSize) {
      message.error('文件超出10mb，请重新上传')
      fileList.value = model.value
        ? fileList.value.filter((item) => item.url === model.value)
        : []
      onError()
      return
    }

    const formData = new FormData()
    formData.append('file', raw)
    uploading.value = true
    file.status = 'uploading'
    try {
      const res = await $fetch<{ ok: boolean; url: string; name: string }>('/api/admin/upload/image', {
        method: 'POST',
        body: formData,
      })
      model.value = res.url
      file.url = res.url
      file.name = res.name || raw.name
      file.status = 'finished'
      onFinish()
    } catch (err: unknown) {
      const statusMessage =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { statusMessage?: string } }).data?.statusMessage || '')
          : ''
      message.error(statusMessage || '图片上传失败')
      model.value = ''
      file.status = 'error'
      onError()
    } finally {
      uploading.value = false
    }
  }

  function onRemove() {
    model.value = ''
    return true
  }
</script>

<template>
  <div class="admin-image-upload">
    <div class="admin-image-upload__wrap">
      <n-upload
        v-model:file-list="fileList"
        :max="1"
        :accept="accept"
        list-type="image-card"
        :disabled="disabled || uploading"
        :custom-request="customRequest"
        :on-before-upload="beforeUpload"
        @remove="onRemove"
      />
      <div v-if="uploading" class="admin-image-upload__loading">
        <n-spin size="medium" />
        <span class="admin-image-upload__loading-text">上传中...</span>
      </div>
    </div>
    <div v-if="tip" class="admin-image-upload__tip">{{ tip }}</div>
  </div>
</template>

<style lang="scss" scoped>
  .admin-image-upload__wrap {
    position: relative;
    display: inline-block;
  }

  .admin-image-upload__loading {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border-radius: 4px;
    background: rgb(255 255 255 / 72%);
    pointer-events: none;
  }

  .admin-image-upload__loading-text {
    font-size: 12px;
    color: #6b7280;
  }

  .admin-image-upload__tip {
    margin-top: 8px;
    font-size: 12px;
    line-height: 1.4;
    color: #9ca3af;
  }
</style>
